import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
/**
 * `LazyPlaceholder` is a simple implementation of IntersectionObserver, which cleans up listeners after triggering visibility.
 */
const LazyPlaceholder = ({
  children,
  container,
  offset,
  onVisible,
  className
}) => {
  const placeholderRef = useRef(null);

  useEffect(() => {
    const element = placeholderRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onVisible();
          }
        });
      },
      {
        root: container,
        rootMargin: `${offset}px`
      }
    );

    if (element) {
      observer.observe(element);
    }

    return () => {
      observer.unobserve(element);
    };
  }, []);

  return (
    <span ref={placeholderRef} className={className}>
      {children}
    </span>
  );
};

LazyPlaceholder.propTypes = {
  /**
   * Element to display before lazily-loaded content is displayed.
   */
  children: PropTypes.node,
  /**
   * Container whose bounding rectangle is used to be considered the viewport.
   */
  container: PropTypes.node,
  /**
   * Offset of bounding box for lazily-loaded content. This is also known as `rootMargin`.
   */
  offset: PropTypes.number.isRequired,
  /**
   * Callback fired when visibility is triggered
   */
  onVisible: PropTypes.func.isRequired,
  /**
   * An element class, used to pass styles with styled-components
   */
  className: PropTypes.string
};
LazyPlaceholder.defaultProps = {
  children: null,
  container: null,
  className: null
};

/**
 * A `LazyLoad` component is used to lazily load any component / content.
 */
const LazyLoad = ({ children, placeholder, offset, container, className }) => {
  const [isVisible, setIsVisible] = useState(false);
  const handleVisible = useCallback(() => setIsVisible(true), []);

  if (isVisible) {
    return children;
  }

  return (
    <LazyPlaceholder
      offset={offset}
      onVisible={handleVisible}
      container={container}
      className={className}
    >
      {placeholder}
    </LazyPlaceholder>
  );
};

LazyLoad.propTypes = {
  /**
   * Content to lazily-load.
   */
  children: PropTypes.node.isRequired,
  /**
   * Container whose bounding rectangle is used to be considered the viewport.
   */
  container: PropTypes.node,
  /**
   * Content to display before lazily-loaded content is displayed.
   */
  placeholder: PropTypes.node,
  /**
   * Offset of bounding box for lazily-loaded content. This is also known as `rootMargin`.
   */
  offset: PropTypes.number,
  /**
   * An element class, used to pass styles with styled-components
   */
  className: PropTypes.string
};

LazyLoad.defaultProps = {
  container: null,
  placeholder: null,
  offset: 100,
  className: null
};

LazyLoad.Placeholder = LazyPlaceholder;

export default LazyLoad;
